/**
 * Project overview panel (v2.5 UX overhaul).
 *
 * Default landing section for a project. Shows header stats + primary
 * actions + recent activity merged from documents and conversations.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PersonaPickerModal from "../components/PersonaPickerModal.jsx";
import { SALES_STAGES } from "../constants/salesStages.js";
import { useStore } from "../store/useStore.js";
import * as projectsApi from "../services/projects.js";

const RELATIVE_UNITS = [
  { limit: 60, divisor: 1, unit: "second" },
  { limit: 3600, divisor: 60, unit: "minute" },
  { limit: 86400, divisor: 3600, unit: "hour" },
  { limit: 2592000, divisor: 86400, unit: "day" },
  { limit: 31536000, divisor: 2592000, unit: "month" },
  { limit: Infinity, divisor: 31536000, unit: "year" },
];

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function formatRelative(iso) {
  if (!iso) return null;
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  const abs = Math.abs(diffSec);
  const { divisor, unit } = RELATIVE_UNITS.find((u) => abs < u.limit);
  return rtf.format(-Math.round(diffSec / divisor), unit);
}

export default function ProjectOverviewTab() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const currentProject = useStore((s) => s.currentProject);
  const documents = useStore((s) => s.documents);
  const conversations = useStore((s) => s.conversations);
  const jobs = useStore((s) => s.jobs);
  const loadDocuments = useStore((s) => s.loadDocuments);
  const loadConversations = useStore((s) => s.loadConversations);
  const fetchJobs = useStore((s) => s.fetchJobs);
  const updateProjectStage = useStore((s) => s.updateProjectStage);
  const pushToast = useStore((s) => s.pushToast);

  const [pickerOpen, setPickerOpen] = useState(false);

  const handleStageChange = async (newStage) => {
    const oldStage = currentProject.salesStage ?? 0;
    updateProjectStage(projectId, newStage);
    try {
      await projectsApi.updateProject(projectId, { salesStage: newStage });
    } catch {
      updateProjectStage(projectId, oldStage);
      pushToast("Failed to update stage", "error");
    }
  };

  useEffect(() => {
    loadDocuments(projectId);
    loadConversations(projectId);
    fetchJobs(projectId);
  }, [projectId, loadDocuments, loadConversations, fetchJobs]);

  const lastJob = useMemo(() => {
    const terminal = jobs.filter((j) =>
      ["completed", "failed", "cancelled"].includes(j.status),
    );
    return terminal.length
      ? terminal.sort(
          (a, b) =>
            new Date(b.completedAt || b.updatedAt || 0) -
            new Date(a.completedAt || a.updatedAt || 0),
        )[0]
      : null;
  }, [jobs]);

  const recentActivity = useMemo(() => {
    const docItems = documents.map((d) => ({
      kind: "document",
      id: d.filename,
      title: d.filename.split("/").pop(),
      timestamp: d.lastModified,
      href: `/projects/${projectId}/editor?filename=${encodeURIComponent(d.filename)}`,
    }));
    const convItems = conversations.map((c) => ({
      kind: "conversation",
      id: c.id,
      title: c.title || "Untitled conversation",
      timestamp: c.updatedAt,
      href:
        c.type === "roundtable"
          ? `/projects/${projectId}/roundtable/${c.id}`
          : `/projects/${projectId}/chat/${c.personaId}/${c.id}`,
    }));
    return [...docItems, ...convItems]
      .filter((i) => i.timestamp)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);
  }, [documents, conversations, projectId]);

  if (!currentProject) return null;

  return (
    <div className="project-overview">
      <div className="overview-stats">
        <StatCard label="Documents" value={documents.length} />
        <StatCard label="Conversations" value={conversations.length} />
        <StatCard
          label="Last pipeline"
          value={lastJob ? formatRelative(lastJob.completedAt) || "—" : "None"}
          sub={lastJob?.status}
        />
      </div>

      <div className="overview-stage">
        <label className="overview-stage__label" htmlFor="stage-selector">
          Sales stage
        </label>
        <select
          id="stage-selector"
          className="stage-selector"
          value={currentProject.salesStage ?? 0}
          onChange={(e) => handleStageChange(Number(e.target.value))}
        >
          {SALES_STAGES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.name} — {s.subtitle}
            </option>
          ))}
        </select>
      </div>

      <div className="overview-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setPickerOpen(true)}
        >
          New conversation
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate(`/projects/${projectId}/jobs`)}
        >
          Run pipeline
        </button>
      </div>

      <section className="overview-recent">
        <h3>Recent activity</h3>
        {recentActivity.length === 0 ? (
          <p className="overview-empty">
            Upload a document or start a conversation to see activity here.
          </p>
        ) : (
          <ul className="overview-recent-list">
            {recentActivity.map((item) => (
              <li key={`${item.kind}-${item.id}`}>
                <button
                  type="button"
                  className="overview-recent-item"
                  onClick={() => navigate(item.href)}
                >
                  <span className={`overview-recent-kind kind-${item.kind}`}>
                    {item.kind === "document" ? "doc" : "chat"}
                  </span>
                  <span className="overview-recent-title">{item.title}</span>
                  <span className="overview-recent-time">
                    {formatRelative(item.timestamp)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <PersonaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        projectId={projectId}
      />
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  );
}
